import type { SoapWorkbenchStreamEvent } from "../../../shared/soap-workbench";

export class SoapWorkbenchStreamError extends Error {
  constructor() {
    super("The model response could not be read.");
    this.name = "SoapWorkbenchStreamError";
  }
}

function isObject(value: unknown): value is Record<string, unknown> {
  return value !== null && typeof value === "object" && !Array.isArray(value);
}

function isUsage(value: unknown): boolean {
  return isObject(value)
    && (typeof value.inputTokens === "number" || value.inputTokens === null)
    && (typeof value.outputTokens === "number" || value.outputTokens === null);
}

function parseEvent(line: string): SoapWorkbenchStreamEvent {
  let value: unknown;
  try {
    value = JSON.parse(line);
  } catch {
    throw new SoapWorkbenchStreamError();
  }
  if (!isObject(value) || typeof value.type !== "string") throw new SoapWorkbenchStreamError();
  switch (value.type) {
    case "heartbeat":
      return { type: "heartbeat" };
    case "started":
      if (typeof value.effectiveSetting !== "string") throw new SoapWorkbenchStreamError();
      return { type: "started", effectiveSetting: value.effectiveSetting };
    case "text_delta":
      if (typeof value.delta !== "string") throw new SoapWorkbenchStreamError();
      return { type: "text_delta", delta: value.delta };
    case "completed":
      if (
        typeof value.durationMs !== "number"
        || typeof value.effectiveSetting !== "string"
        || !isUsage(value.usage)
      ) throw new SoapWorkbenchStreamError();
      return {
        type: "completed",
        durationMs: value.durationMs,
        usage: {
          inputTokens: (value.usage as Record<string, unknown>).inputTokens as number | null,
          outputTokens: (value.usage as Record<string, unknown>).outputTokens as number | null,
        },
        effectiveSetting: value.effectiveSetting,
      };
    case "failed":
      if (
        typeof value.category !== "string"
        || typeof value.message !== "string"
        || typeof value.durationMs !== "number"
      ) throw new SoapWorkbenchStreamError();
      return {
        type: "failed",
        category: value.category,
        message: value.message,
        durationMs: value.durationMs,
      };
    default:
      // Unknown events fail closed so a future provider stream cannot silently
      // introduce reasoning or metadata into this PHI-bearing surface.
      throw new SoapWorkbenchStreamError();
  }
}

export async function* readSoapWorkbenchStream(
  body: ReadableStream<Uint8Array> | null,
): AsyncIterable<SoapWorkbenchStreamEvent> {
  if (!body) throw new SoapWorkbenchStreamError();
  const reader = body.getReader();
  const decoder = new TextDecoder();
  let buffer = "";
  let terminal = false;
  try {
    while (true) {
      const { done, value } = await reader.read();
      buffer += decoder.decode(value, { stream: !done });
      const lines = buffer.split("\n");
      buffer = lines.pop() ?? "";
      for (const line of lines) {
        if (!line.trim()) continue;
        if (terminal) throw new SoapWorkbenchStreamError();
        const event = parseEvent(line);
        if (event.type === "completed" || event.type === "failed") terminal = true;
        yield event;
      }
      if (done) break;
    }
    if (buffer.trim()) {
      if (terminal) throw new SoapWorkbenchStreamError();
      const event = parseEvent(buffer);
      if (event.type === "completed" || event.type === "failed") terminal = true;
      yield event;
    }
    if (!terminal) throw new SoapWorkbenchStreamError();
  } finally {
    reader.releaseLock();
  }
}
